/**
 * Carbon Labs — first-party event tracker.
 *
 * Persistent visitor_id (localStorage) + per-tab session_id (sessionStorage,
 * rotated after 30 minutes of idle). Fires page_view on route change and a
 * handful of e-commerce events via the exported `track()` helper.
 *
 * Optional Google Analytics 4 forwarding: set window.__GA4_ID__ or pass
 * the measurement_id via initTracker({ ga4MeasurementId }).
 */
import api from "./api";

const VISITOR_KEY = "vortex_visitor_id";
const SESSION_KEY = "vortex_session_id";
const SESSION_LAST_KEY = "vortex_session_last";
const IDLE_MS = 30 * 60 * 1000; // 30 min

function uuid() {
    // RFC4122-ish — good enough for anonymous tracking
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

export function getVisitorId() {
    try {
        let v = localStorage.getItem(VISITOR_KEY);
        if (!v) {
            v = uuid();
            localStorage.setItem(VISITOR_KEY, v);
        }
        return v;
    } catch {
        return uuid();
    }
}

export function getSessionId() {
    try {
        const last = parseInt(sessionStorage.getItem(SESSION_LAST_KEY) || "0", 10);
        const now = Date.now();
        let s = sessionStorage.getItem(SESSION_KEY);
        if (!s || (last && now - last > IDLE_MS)) {
            s = uuid();
            sessionStorage.setItem(SESSION_KEY, s);
        }
        sessionStorage.setItem(SESSION_LAST_KEY, String(now));
        return s;
    } catch {
        return uuid();
    }
}

/* ── User-Agent based device detection (good enough w/o external libs) ── */
function detectClient() {
    const ua = navigator.userAgent || "";
    const isTablet = /iPad|Tablet|Nexus 7|Nexus 9|Nexus 10|Kindle/i.test(ua);
    const isMobile = !isTablet && /Mobile|iPhone|Android.*Mobile|Phone|IEMobile|Opera Mini/i.test(ua);
    const device = isTablet ? "tablet" : isMobile ? "mobile" : "desktop";

    let browser = "Other";
    if (/Edg\//.test(ua)) browser = "Edge";
    else if (/OPR\//.test(ua)) browser = "Opera";
    else if (/Chrome\//.test(ua)) browser = "Chrome";
    else if (/Firefox\//.test(ua)) browser = "Firefox";
    else if (/Safari\//.test(ua)) browser = "Safari";

    let os = "Other";
    if (/Windows/.test(ua)) os = "Windows";
    else if (/Mac OS X/.test(ua)) os = "macOS";
    else if (/Android/.test(ua)) os = "Android";
    else if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";
    else if (/Linux/.test(ua)) os = "Linux";

    const screen = `${window.screen?.width || 0}x${window.screen?.height || 0}`;
    return { device, browser, os, screen };
}

function getUTMParams() {
    try {
        const params = new URLSearchParams(window.location.search);
        return {
            utm_source: params.get("utm_source") || undefined,
            utm_medium: params.get("utm_medium") || undefined,
            utm_campaign: params.get("utm_campaign") || undefined,
        };
    } catch {
        return {};
    }
}

let _ga4Id = null;
let _ga4Loaded = false;

function loadGA4(measurementId) {
    if (_ga4Loaded || !measurementId || typeof document === "undefined") return;
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
        window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("config", measurementId, { anonymize_ip: true });
    _ga4Loaded = true;
}

export function initTracker(opts = {}) {
    if (opts.ga4MeasurementId) {
        _ga4Id = opts.ga4MeasurementId;
        loadGA4(_ga4Id);
    } else if (window.__GA4_ID__) {
        _ga4Id = window.__GA4_ID__;
        loadGA4(_ga4Id);
    }
}

// Defense-in-depth: never send tracking events for paths that look like
// vulnerability scanner probes (Spring Actuator, Laravel Horizon, CGI, .env
// leaks, wp-*, command-injection ?cmd=...). Bots that happen to execute JS on
// the SPA catch-all still get their pageview dropped client-side. The backend
// applies the same filter at /api/track/event as a second layer.
const SCANNER_PATH_RE = /^\/(?:actuator(?:\/|$)|horizon(?:\/|$)|telescope(?:\/|$)|cgi-bin(?:\/|$)|system(?:\?|\/|$)|run(?:\?|\/|$)|shell(?:\?|\/|$)|cmd(?:\?|\/|$)|exec(?:\?|\/|$)|\.env|\.git(?:\/|$)|\.svn(?:\/|$)|\.aws(?:\/|$)|\.ssh(?:\/|$)|\.htaccess|\.htpasswd|config\.php|configuration\.php|wp-config\.php|web\.config|wp-login\.php|wp-admin(?:\/|$)|xmlrpc\.php|phpmyadmin(?:\/|$)|phpinfo\.php|adminer\.php|wordpress(?:\/|$)|struts(?:\/|$)|_ignition|solr(?:\/|$)|manager\/html|dump\.sql|backup\.sql|db\.sql|database\.sql)/i;
const INJECTION_QS_RE = /(?:^|&)(?:cmd|run|exec|shell|command|q)=.*(?:[;&|`$]|%3B|%26|%7C|%60|%24|\becho\b|\bcat\b|\bwget\b|\bcurl\b|\bnc\b|\bbash\b|\bsh\b|\bperl\b|\bpython\b)/i;

function isScannerPath(fullPath) {
    if (!fullPath) return false;
    const [p, qs] = fullPath.split("?");
    if (SCANNER_PATH_RE.test(p)) return true;
    if (qs && INJECTION_QS_RE.test(qs)) return true;
    return false;
}

export async function track(event_type, extra = {}) {
    try {
        const fullPath = window.location.pathname + (window.location.search || "");
        // Scanner-probe URL — do NOT hit the server, do NOT log to analytics
        if (isScannerPath(fullPath)) {
            return;
        }
        const { device, browser, os, screen } = detectClient();
        const utm = getUTMParams();
        const payload = {
            event_type,
            session_id: getSessionId(),
            visitor_id: getVisitorId(),
            path: fullPath,
            page: extra.page || document.title || undefined,
            referrer: document.referrer || undefined,
            device,
            browser,
            os,
            screen,
            ...utm,
            ...extra,
        };
        // Fire-and-forget — never block UX
        api.post("/track/event", payload).catch(() => {});

        // GA4 forwarding (optional)
        if (_ga4Id && window.gtag) {
            const ga4Event = event_type === "page_view" ? "page_view" : event_type;
            window.gtag("event", ga4Event, {
                page_title: payload.page,
                page_location: window.location.href,
                product_slug: extra.product_slug,
                value: extra.value,
                currency: "AUD",
            });
        }
    } catch {
        // Tracking must never break the page
    }
}

export function trackPageView() {
    return track("page_view");
}

export function trackProductView(product_slug, name) {
    return track("product_view", { product_slug, page: name });
}

export function trackAddToCart(product_slug, name) {
    return track("add_to_cart", { product_slug, page: name });
}

export function trackCheckoutStart(value) {
    return track("checkout_start", { value });
}

export function trackPurchase(value, reference) {
    return track("purchase", { value, page: reference });
}
